/**
 * Monetag Rewarded Interstitial Ads Integration Hook
 * Zone ID: 10377230
 * 
 * Supports:
 * - Rewarded Interstitial Ads
 * 
 * Integration:
 * - SDK loads from //libtl.com/sdk.js with data-zone="10377230" data-sdk="show_10377230"
 * - Show function: window.show_10377230()
 * - Returns Promise that resolves when ad is watched
 */

import { useConfigStore } from '../store/useConfigStore';

// Default Monetag Zone ID
export const MONETAG_ZONE_ID = '10377230';

declare global {
  interface Window {
    show_10377230?: () => Promise<void>;
    [key: `show_${string}`]: (() => Promise<void>) | undefined;
  }
}

/**
 * Check if Monetag is enabled in config
 */
function isMontagEnabled(): boolean {
  const config = useConfigStore.getState();
  return config.adsEnabled && (config.monetag?.enabled ?? true);
}

/**
 * Get the Monetag show function based on zone ID
 */
function getMontagShowFunction(zoneId?: string): (() => Promise<void>) | undefined {
  const zone = zoneId || MONETAG_ZONE_ID;
  const fnName = `show_${zone}` as keyof Window;
  return window[fnName] as (() => Promise<void>) | undefined;
}

/**
 * Check if Monetag SDK is loaded
 */
export function isMontagLoaded(zoneId?: string): boolean {
  return typeof getMontagShowFunction(zoneId) === 'function';
}

/**
 * Show Monetag Rewarded Interstitial Ad
 * Returns true if ad was shown and watched successfully, false otherwise
 * 
 * @param zoneId - Optional zone ID (defaults to 10377230)
 */
export async function showMontagRewardedInterstitial(zoneId?: string): Promise<boolean> {
  if (!isMontagEnabled()) {
    console.warn('[Monetag] Ads are disabled');
    return false;
  }

  const showFn = getMontagShowFunction(zoneId);

  if (!showFn) {
    console.error('[Monetag] SDK not loaded or show function not available');
    return false;
  }

  try {
    console.log('[Monetag] Starting rewarded interstitial ad...');
    await showFn();
    console.log('[Monetag] Ad watched successfully');
    return true;
  } catch (error) {
    console.error('[Monetag] Failed to show ad:', error);
    return false;
  }
}

/**
 * Hook for Monetag rewarded interstitial ads
 * Provides reactive state and show function
 */
export function useMonetag(zoneId?: string) {
  const zone = zoneId || MONETAG_ZONE_ID;
  const config = useConfigStore((state) => state.monetag);
  const adsEnabled = useConfigStore((state) => state.adsEnabled);

  const isEnabled = adsEnabled && (config?.enabled ?? true);
  const isLoaded = isMontagLoaded(zone);
  const rewardAmount = config?.rewardAmount ?? 50;

  const showAd = async (): Promise<boolean> => {
    if (!isEnabled) {
      console.warn('[useMonetag] Monetag is disabled');
      return false;
    }
    return showMontagRewardedInterstitial(zone);
  };

  return {
    showAd,
    isEnabled,
    isLoaded,
    rewardAmount,
    zoneId: zone,
  };
}

export default useMonetag;
